import React, { useEffect, useState, useCallback } from 'react';
import './InstallPWA.css';

// Extend the global Window interface for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const InstallPWA: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Check if already installed or previously dismissed
  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const dismissedAt = localStorage.getItem('pwa-install-dismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < threeDaysMs) {
        return; // Don't show for 3 days after dismiss
      }
    }

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Small delay so the page loads first
      setTimeout(() => setShowBanner(true), 2000);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowBanner(false);
      setDeferredPrompt(null);
      localStorage.removeItem('pwa-install-dismissed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
    } catch (err) {
      console.error('Install prompt failed:', err);
    } finally {
      setIsInstalling(false);
      setShowBanner(false);
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setIsClosing(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setTimeout(() => {
      setShowBanner(false);
      setIsClosing(false);
    }, 400);
  }, []);

  // Don't render anything if already installed or no prompt available
  if (isInstalled || !showBanner) return null;

  return (
    <div className={`pwa-install-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`pwa-install-banner ${isClosing ? 'closing' : ''}`}>
        {/* Decorative background elements */}
        <div className="pwa-banner-glow pwa-glow-1" />
        <div className="pwa-banner-glow pwa-glow-2" />

        {/* Close button */}
        <button
          className="pwa-close-btn"
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          id="pwa-dismiss-btn"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* App icon */}
        <div className="pwa-app-icon-wrapper">
          <div className="pwa-app-icon-ring" />
          <div className="pwa-app-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="url(#bolt-gradient)" />
              <defs>
                <linearGradient id="bolt-gradient" x1="3" y1="2" x2="22" y2="24" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#818cf8" />
                  <stop offset="0.5" stopColor="#a78bfa" />
                  <stop offset="1" stopColor="#f472b6" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="pwa-banner-content">
          <h3 className="pwa-banner-title">
            Install <span className="pwa-title-gradient">SmartShrink</span>
          </h3>
          <p className="pwa-banner-description">
            Get instant access from your home screen. Works offline, loads faster, and feels like a native app.
          </p>
        </div>

        {/* Feature pills */}
        <div className="pwa-feature-pills">
          <div className="pwa-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Lightning Fast
          </div>
          <div className="pwa-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Works Offline
          </div>
          <div className="pwa-pill">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="18" x2="12" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Native Feel
          </div>
        </div>

        {/* Action buttons */}
        <div className="pwa-banner-actions">
          <button
            className="pwa-install-btn"
            onClick={handleInstall}
            disabled={isInstalling}
            id="pwa-install-btn"
          >
            {isInstalling ? (
              <>
                <span className="pwa-spinner" />
                Installing...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Install App
              </>
            )}
          </button>
          <button
            className="pwa-later-btn"
            onClick={handleDismiss}
            id="pwa-later-btn"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
